/**
 * TanStack Query Cache Coordination Utilities
 *
 * Provides reusable patterns for surgical cache updates, eliminating
 * global invalidation anti-patterns and reducing unnecessary re-renders.
 *
 * @see docs/cache-optimization-patterns.md for detailed examples
 */

import {type QueryClient} from '@tanstack/react-query';

/**
 * Entity with required ID field
 */
export type Entity = {
  [key: string]: any;
  id: string;
};

/**
 * Paginated list response structure
 */
export type PaginatedList<T> = {
  [key: string]: any;
  items: T[];
  total: number;
};

/**
 * Context returned from optimistic updates for rollback
 */
export type OptimisticContext<T = any> = {
  previous: T | undefined;
};

/**
 * Options for entity cache operations
 */
export type EntityCacheOptions = {
  /** Skip invalidation in onSettled (for pure optimistic updates) */
  skipInvalidation?: boolean;
  /** Custom query key generator */
  queryKeyFn?: (id: string, tenantId: string) => unknown[];
};

/**
 * Creates a surgical cache updater for a specific entity type
 *
 * Eliminates global invalidations by providing targeted setQueryData operations.
 *
 * @example
 * ```typescript
 * const documentCache = createEntityCache<Document>(queryClient, 'document', 'documents');
 *
 * // Update single entity in cache
 * documentCache.updateSingle(documentId, tenantId, (old) => ({
 *   ...old,
 *   status: 'published'
 * }));
 *
 * // Update entity within list cache
 * documentCache.updateInList(tenantId, documentId, (old) => ({
 *   ...old,
 *   status: 'published'
 * }));
 * ```
 */
export function createEntityCache<T extends Entity>(
  queryClient: QueryClient,
  entityKey: string,
  listKey: string
) {
  return {
    /**
     * Update single entity in cache
     */
    updateSingle(
      id: string,
      tenantId: string,
      updater: (old: T) => T,
      options?: EntityCacheOptions
    ) {
      const queryKey = options?.queryKeyFn?.(id, tenantId) ?? [
        entityKey,
        id,
        tenantId,
      ];
      queryClient.setQueryData<T>(queryKey, (old) => {
        if (!old) return old;
        return updater(old);
      });
    },

    /**
     * Update entity within list cache
     */
    updateInList(tenantId: string, id: string, updater: (old: T) => T) {
      queryClient.setQueryData<PaginatedList<T>>([listKey, tenantId], (old) => {
        if (!old?.items) return old;
        return {
          ...old,
          items: old.items.map((item) =>
            item.id === id ? updater(item) : item
          ),
        };
      });
    },

    /**
     * Add entity to list cache (for create operations)
     */
    addToList(tenantId: string, newEntity: T) {
      queryClient.setQueryData<PaginatedList<T>>([listKey, tenantId], (old) => {
        if (!old?.items) return old;
        return {
          ...old,
          items: [newEntity, ...old.items],
          total: old.total + 1,
        };
      });
    },

    /**
     * Remove entity from list cache (for delete operations)
     */
    removeFromList(tenantId: string, id: string) {
      queryClient.setQueryData<PaginatedList<T>>([listKey, tenantId], (old) => {
        if (!old?.items) return old;
        return {
          ...old,
          items: old.items.filter((item) => item.id !== id),
          total: Math.max(0, old.total - 1),
        };
      });
    },

    /**
     * Cancel ongoing queries to prevent race conditions
     */
    async cancelQueries(id: string | undefined, tenantId: string) {
      if (id) {
        await queryClient.cancelQueries({
          queryKey: [entityKey, id, tenantId],
        });
      }

      await queryClient.cancelQueries({
        queryKey: [listKey, tenantId],
      });
    },

    /**
     * Get current cache data for rollback
     */
    getSnapshots(id: string | undefined, tenantId: string) {
      const single = id
        ? queryClient.getQueryData<T>([entityKey, id, tenantId])
        : undefined;
      const list = queryClient.getQueryData<PaginatedList<T>>([
        listKey,
        tenantId,
      ]);

      return {single, list};
    },

    /**
     * Restore cache data on error (rollback)
     */
    restoreSnapshots(
      id: string | undefined,
      tenantId: string,
      snapshots: {single?: T; list?: PaginatedList<T>}
    ) {
      if (id && snapshots.single) {
        queryClient.setQueryData([entityKey, id, tenantId], snapshots.single);
      }

      if (snapshots.list) {
        queryClient.setQueryData([listKey, tenantId], snapshots.list);
      }
    },

    /**
     * Invalidate specific queries (use sparingly, prefer setQueryData)
     *
     * ⚠️ Only use in onSettled for edge cases where:
     * 1. Server response significantly differs from optimistic update
     * 2. Related data needs refreshing (e.g., computed aggregates)
     */
    invalidate(id: string | undefined, tenantId: string) {
      if (id) {
        void queryClient.invalidateQueries({
          queryKey: [entityKey, id, tenantId],
        });
      }

      void queryClient.invalidateQueries({
        queryKey: [listKey, tenantId],
      });
    },
  };
}

/**
 * Cascade update for related entities
 *
 * Updates multiple cache entries in a coordinated way to maintain consistency
 * across related entities (e.g., approval → revision → document).
 *
 * @example
 * ```typescript
 * cascadeUpdate(queryClient, [
 *   {
 *     queryKey: ['approval', revisionId, tenantId],
 *     updater: (old: Approval) => ({ ...old, status: 'approved' })
 *   },
 *   {
 *     queryKey: ['revision', revisionId, documentId],
 *     updater: (old: Revision) => ({ ...old, status: 'approved' })
 *   },
 *   {
 *     queryKey: ['document', documentId, tenantId],
 *     updater: (old: Document) => ({ ...old, status: 'approved' })
 *   }
 * ]);
 * ```
 */
export function cascadeUpdate<T = any>(
  queryClient: QueryClient,
  updates: Array<{
    queryKey: unknown[];
    updater: (old: T) => T;
  }>
) {
  for (const {queryKey, updater} of updates) {
    queryClient.setQueryData<T>(queryKey, (old) => {
      if (!old) return old;
      return updater(old);
    });
  }
}

/**
 * Optimistic update helper with automatic rollback
 *
 * Provides a complete optimistic update pattern with:
 * - Query cancellation
 * - Snapshot for rollback
 * - Cache update
 * - Context for error handling
 *
 * @example
 * ```typescript
 * const mutation = useMutation({
 *   mutationFn: updateDocumentApi,
 *   onMutate: async (variables) => {
 *     return performOptimisticUpdate(
 *       queryClient,
 *       ['document', documentId, tenantId],
 *       (old: Document) => ({ ...old, name: variables.name })
 *     );
 *   },
 *   onError: (err, variables, context) => {
 *     rollbackOptimisticUpdate(
 *       queryClient,
 *       ['document', documentId, tenantId],
 *       context
 *     );
 *   }
 * });
 * ```
 */
export async function performOptimisticUpdate<T>(
  queryClient: QueryClient,
  queryKey: unknown[],
  updater: (old: T) => T
): Promise<OptimisticContext<T>> {
  // Cancel ongoing queries to prevent race conditions
  await queryClient.cancelQueries({queryKey});

  // Snapshot for rollback
  const previous = queryClient.getQueryData<T>(queryKey);

  // Apply optimistic update
  queryClient.setQueryData<T>(queryKey, (old) => {
    if (!old) return old;
    return updater(old);
  });

  return {previous};
}

/**
 * Rollback optimistic update on error
 */
export function rollbackOptimisticUpdate<T>(
  queryClient: QueryClient,
  queryKey: unknown[],
  context: OptimisticContext<T> | undefined
) {
  if (context?.previous !== undefined) {
    queryClient.setQueryData(queryKey, context.previous);
  }
}

/**
 * Batch multiple optimistic updates with coordinated rollback
 *
 * @example
 * ```typescript
 * const mutation = useMutation({
 *   onMutate: async (variables) => {
 *     return performBatchOptimisticUpdate(queryClient, [
 *       {
 *         queryKey: ['document', documentId, tenantId],
 *         updater: (old: Document) => ({ ...old, status: 'published' })
 *       },
 *       {
 *         queryKey: ['documents', tenantId],
 *         updater: (old: Document[]) =>
 *           old.map(doc => doc.id === documentId ? { ...doc, status: 'published' } : doc)
 *       }
 *     ]);
 *   },
 *   onError: (err, variables, context) => {
 *     rollbackBatchOptimisticUpdate(queryClient, context);
 *   }
 * });
 * ```
 */
export async function performBatchOptimisticUpdate(
  queryClient: QueryClient,
  updates: Array<{
    queryKey: unknown[];
    updater: (old: any) => any;
  }>
): Promise<OptimisticContext[]> {
  return Promise.all(
    updates.map(async ({queryKey, updater}) =>
      performOptimisticUpdate(queryClient, queryKey, updater)
    )
  );
}

/**
 * Rollback batch optimistic updates
 */
export function rollbackBatchOptimisticUpdate(
  queryClient: QueryClient,
  contexts: Array<{queryKey: unknown[]; context: OptimisticContext}>
) {
  for (const {queryKey, context} of contexts) {
    rollbackOptimisticUpdate(queryClient, queryKey, context);
  }
}

/**
 * Selective invalidation helper
 *
 * Invalidates only specific related queries, avoiding broad predicates.
 *
 * @example
 * ```typescript
 * // ❌ BAD: Nuclear invalidation
 * queryClient.invalidateQueries({
 *   predicate: (query) => query.queryKey[0] === 'documents'
 * });
 *
 * // ✅ GOOD: Targeted invalidation
 * invalidateRelatedQueries(queryClient, [
 *   ['document', documentId, tenantId],
 *   ['documents', tenantId],
 *   ['revisions', documentId]
 * ]);
 * ```
 */
export function invalidateRelatedQueries(
  queryClient: QueryClient,
  queryKeys: unknown[][]
) {
  for (const queryKey of queryKeys) {
    void queryClient.invalidateQueries({queryKey});
  }
}

/**
 * Remove entity from all matching query caches
 *
 * Uses setQueriesData to update all queries matching a base key prefix.
 * Useful for query hooks that use dynamic keys (e.g., with filters/pagination).
 *
 * @example
 * ```typescript
 * // Remove document from all useDocumentsQuery caches
 * removeFromDocumentsQuery<DocumentList>(
 *   queryClient,
 *   documentKeys.queries(tenantId), // ['documents', 'query', tenantId]
 *   documentId
 * );
 * ```
 */
export function removeFromDocumentsQuery<T extends PaginatedList<Entity>>(
  queryClient: QueryClient,
  queryKeyPrefix: readonly unknown[],
  id: string
) {
  queryClient.setQueriesData<T>(
    {queryKey: queryKeyPrefix},
    (old): T | undefined => {
      if (!old?.items) return old;
      const result: T = {
        ...old,
        items: old.items.filter((item) => item.id !== id),
        total: Math.max(0, old.total - 1),
      };
      return result;
    }
  );
}

/**
 * Add entity to all matching query caches
 *
 * Uses setQueriesData to update all queries matching a base key prefix.
 * Useful for query hooks that use dynamic keys (e.g., with filters/pagination).
 *
 * @example
 * ```typescript
 * // Add document to all useDocumentsQuery caches
 * addToDocumentsQuery<DocumentList>(
 *   queryClient,
 *   documentKeys.queries(tenantId), // ['documents', 'query', tenantId]
 *   newDocument
 * );
 * ```
 */
export function addToDocumentsQuery<T extends PaginatedList<Entity>>(
  queryClient: QueryClient,
  queryKeyPrefix: readonly unknown[],
  newEntity: Entity
) {
  queryClient.setQueriesData<T>(
    {queryKey: queryKeyPrefix},
    (old): T | undefined => {
      if (!old?.items) return old;
      const result: T = {
        ...old,
        items: [newEntity, ...old.items],
        total: old.total + 1,
      };
      return result;
    }
  );
}

/**
 * Update entity in all matching query caches
 *
 * Uses setQueriesData to update all queries matching a base key prefix.
 * Useful for query hooks that use dynamic keys (e.g., with filters/pagination).
 *
 * @example
 * ```typescript
 * // Update document in all useDocumentsQuery caches
 * updateInDocumentsQuery<DocumentList>(
 *   queryClient,
 *   documentKeys.queries(tenantId), // ['documents', 'query', tenantId]
 *   documentId,
 *   (old) => ({ ...old, status: 'retired' })
 * );
 * ```
 */
export function updateInDocumentsQuery<T extends PaginatedList<Entity>>(
  queryClient: QueryClient,
  queryKeyPrefix: readonly unknown[],
  id: string,
  updater: (old: Entity) => Entity
) {
  queryClient.setQueriesData<T>(
    {queryKey: queryKeyPrefix},
    (old): T | undefined => {
      if (!old?.items) return old;
      const result: T = {
        ...old,
        items: old.items.map((item) => (item.id === id ? updater(item) : item)),
      };
      return result;
    }
  );
}
